import React, { useEffect, useState, ReactElement, useContext } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';

import {
  range,
  sample,
  get,
  replace,
  split,
  toLower,
  startCase,
  isEmpty,
  map,
  isArray,
  includes,
  size,
  startsWith,
  find,
} from 'lodash';
import { differenceInYears, parseISO } from 'date-fns';
import { useQuery } from '@apollo/client';
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer, Legend } from 'recharts';
import clsx from 'clsx';

import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AddRounded } from '@material-ui/icons';

import ShopComponent from 'components/shop';
import { LatestAnswers } from 'types';
import { Context } from 'pages/_app';
import ImageUploader from '../components/image-uploader';
import ViewLoading from 'components/view-loading';
import { HomeViewDocument } from 'functions/types/graphql/firestore';

const useStyles = makeStyles({
  root: {
    padding: '2rem',
  },
  card: {
    height: '100%',
  },
  paper: {
    padding: '1.5rem',
    maxWidth: '100%',
    height: '100%',
  },
  defaultIcon: {
    width: '7rem',
    height: '7rem',
    color: 'rgba(0,0,0,0.25)',
  },
  fitz: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
  },
  acneCoverageSvg: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      height: '14rem',
    },
  },
});

export default function Home(): ReactElement {
  const classes = useStyles();

  const { is_admin, isAuthenticated, uid } = useContext(Context);

  const { push } = useRouter();
  if (is_admin) push('/admin');

  const { data } = useQuery(HomeViewDocument, {
    variables: { id: uid },
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  const [dialogType, setDialogType] = useState<string>();
  const _closeDialog = () => setDialogType(undefined);

  const { first_name, last_name, email, dob, profile_image, latest_answers } = data?.user || {};
  const formattedName = `${first_name}${last_name ? ` ${last_name}` : ''}`;
  const age = dob ? differenceInYears(new Date(), parseISO(dob)) : false;
  const profileImageUrl = profile_image?.url;

  const action = replace(
    find(split(get(document, 'location.search'), /[?&]/), param => startsWith(param, 'action=')),
    'action=',
    '',
  );
  useEffect(() => {
    if (action === 'profile-upload') setDialogType('profile-upload');
  }, []);

  const { sex, skintype, areas_of_face_get_acne } = latest_answers || {};
  let { fitz } = latest_answers || {};
  if (fitz === 'null') {
    fitz = 1;
  }
  const skintypeLabel =
    skintype === 'dry'
      ? skintype || 'dry'
      : skintype?.toLowerCase().trim() === 'null'
      ? 'normal'
      : skintype || 'normal';
  if (is_admin || !data?.user?.first_name) return <ViewLoading />;

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Typography variant="h5">Overview</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Card variant="outlined" className={classes.card}>
            <CardActionArea onClick={() => setDialogType('profile-upload')}>
              <div className="w-full h-[12rem] sm:h-[16rem] flex justify-center items-center relative hover:opacity-75 transition bg-gray-100 hover:bg-gray-300">
                {profileImageUrl ? (
                  <Image
                    src={profileImageUrl}
                    alt={formattedName}
                    layout="fill"
                    sizes="(min-width: 1200px) 30vw, (min-width: 600px) 50vw, 95vw"
                    objectFit="cover"
                    objectPosition="center"
                  />
                ) : (
                  <AddRounded className={classes.defaultIcon} />
                )}
              </div>
            </CardActionArea>
            <CardContent className={classes.paper}>
              <Typography paragraph variant="h6">
                {!profileImageUrl ? 'Upload Profile Image' : formattedName}
              </Typography>
              <Typography variant="body1">{email}</Typography>
              <Typography variant="body1">Age: {age ? age : 'Missing'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Card variant="outlined" className={classes.card}>
            <CardContent className={classes.paper}>
              <ShopComponent variant="summary" />
            </CardContent>
          </Card>
        </Grid>
        {!isEmpty(latest_answers) && (
          <Grid item xs={12} sm={6} lg={4}>
            <Card variant="outlined" className={classes.card}>
              <CardContent className={classes.paper}>
                <Typography color="primary" variant="subtitle1" align="center">
                  Acne Breakdown
                </Typography>
                <SeverityPieChart latestAnswers={latest_answers} />
              </CardContent>
            </Card>
          </Grid>
        )}
        {fitz && (
          <Grid item xs={12} sm={6} lg={4}>
            <Card variant="outlined" className={classes.card}>
              <CardContent className={clsx(classes.paper, classes.fitz)}>
                <Typography paragraph color="primary" variant="subtitle1" align="center">
                  Fitzpatrick Skin Type {fitz}
                </Typography>
                <Image
                  src={`/assets/svgs/${
                    toLower(String(sex)) === 'female' ? 'female' : 'male'
                  }-fitzpatrick-${fitz}.svg`}
                  layout="fixed"
                  width={14 * 16}
                  height={14 * 16}
                  objectFit="contain"
                  objectPosition="center"
                  alt={`${sex} fitzpatrick type ${fitz} representation`}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {skintypeLabel && (
          <Grid item xs={12} sm={6} lg={4}>
            <Card variant="outlined" className={classes.card}>
              <CardContent className={clsx(classes.paper, classes.fitz)}>
                <Typography paragraph color="primary" variant="subtitle1" align="center">
                  {startCase(String(skintypeLabel))} Skin
                </Typography>
                <Image
                  src={`/assets/svgs/${toLower(String(sex)) === 'female' ? 'female' : 'male'}-${
                    skintype !== 'null' ? skintype : 'dry'
                  }-skin.svg`}
                  width={14 * 16}
                  height={14 * 16}
                  objectFit="contain"
                  objectPosition="center"
                  alt={`${skintypeLabel} skin representation`}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {!isEmpty(areas_of_face_get_acne) && (
          <Grid item xs={12} sm={6} lg={4}>
            <Card variant="outlined" className={classes.card}>
              <CardContent className={clsx(classes.paper, classes.fitz)}>
                <Typography paragraph color="primary" variant="subtitle1" align="center">
                  Acne Coverage
                </Typography>
                <div>
                  <AcneCoverageSvg areas={areas_of_face_get_acne} />
                </div>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      <Dialog open={Boolean(dialogType)} onClose={_closeDialog}>
        {dialogType === 'profile-upload' && (
          <>
            <DialogTitle id="form-dialog-title">Upload Profile Photo</DialogTitle>
            <DialogContent>
              <ImageUploader handleClose={_closeDialog} type="profile" />
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
}

const SeverityPieChart = React.memo(function SeverityPieChart({
  latestAnswers,
}: {
  latestAnswers: LatestAnswers;
}) {
  const data = [
    {
      name: 'Whiteheads / Blackheads',
      value: get(latestAnswers, 'whiteheads_blackheads_currently'),
      colour: '#B5DAD3',
    },
    {
      name: 'Papules / Pustules',
      value: get(latestAnswers, 'papules_pustules_currently'),
      colour: '#6756BE',
    },
    {
      name: 'Nodules',
      value: get(latestAnswers, 'nodules_currently'),
      colour: '#CD7E39',
    },
    {
      name: 'Cysts',
      value: get(latestAnswers, 'cystic_currently') === 'Yes' ? 3 : 0,
      colour: '#8A8CBD',
    },
  ];

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80}>
          {!isEmpty(data) && map(data, ({ colour }, i) => <Cell key={`cell-${i}`} fill={colour} />)}
        </Pie>
        <Tooltip />
        <Legend layout="vertical" formatter={renderLegend} />
      </PieChart>
    </ResponsiveContainer>
  );
});

const renderLegend = (value, entry) => (
  <span>
    {value}: score {get(entry, 'payload.value')}
  </span>
);

const coloursArray = map(range(0, 30), () => sample(['#FCBA19', '#7864C7', '#9CDED5', '#CD7E39']));

function AcneCoverageSvg({ areas }: { areas: boolean | string | string[] | number }) {
  const classes = useStyles();
  const lowercaseAreas = isArray(areas)
    ? map(areas, area => toLower(area))
    : toLower(String(areas));

  return (
    <div className={classes.acneCoverageSvg}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 146 146">
        <g strokeWidth="0" stroke="none">
          <path
            strokeWidth="0"
            fill="#898989"
            fillOpacity="0.6"
            d="m112.97 52.12c-.79-.23-.54-1.53-.27-3 .08-.41.15-.85.22-1.19l.26-1.75c1.3-8.41-.01-15.1-2.9-20.37-3.48-6.4-9.26-10.64-15.46-13.22-5.53-2.31-13.19-3.46-20.85-3.46-7.65.01-15.31 1.17-20.83 3.48-6.19 2.58-11.94 6.82-15.42 13.22-2.86 5.26-4.17 11.97-2.89 20.35l.27 1.75c.05.34.14.77.21 1.19.27 1.47.53 2.77-.26 3-4.49 1.34-5.03 4.49-3.86 8.42.82 2.76 2.55 5.91 4.27 9.04 2.02 3.68 4.04 7.36 4.46 10.2.81 5.34 3.29 11 6.86 16.08 1.41 2 2.98 3.91 4.68 5.66.01.03.04.05.06.08.61.7 1.17 1.91 1.64 3.19.67 1.83 1.11 3.82 1.22 4.87.49 4.42.57 8.45-1.21 12.5-1.79 4.09-5.49 8.27-12.55 13.02-.43.3-.54.85-.26 1.29.3.41.86.53 1.29.24 7.38-4.98 11.27-9.43 13.2-13.81 1.95-4.44 1.88-8.74 1.35-13.45-.1-1.01-.49-2.77-1.06-4.53 1.52 1.24 3.12 2.36 4.77 3.33 1.84 1.08 3.75 1.99 6.01 2.64 2.24.67 4.82 1.1 8.02 1.26h.1c3-.14 5.42-.52 7.5-1.1 2.11-.58 3.87-1.35 5.57-2.28 2.79-1.51 5.42-3.47 7.83-5.73-.43 1.48-.72 2.9-.83 3.75-.52 4.71-.58 9.01 1.38 13.45 1.92 4.38 5.82 8.83 13.2 13.81.41.28.99.17 1.28-.26.28-.41.17-.99-.24-1.28-7.07-4.75-10.77-8.94-12.56-13.02-1.78-4.05-1.72-8.07-1.21-12.5.12-1.06.55-3.03 1.21-4.87.48-1.29 1.04-2.49 1.66-3.2.03-.01.04-.05.06-.08.68-.81 1.34-1.65 1.96-2.53 3.75-5.2 6.36-11.06 7.2-16.56.43-2.84 2.45-6.53 4.46-10.2 1.73-3.13 3.46-6.28 4.27-9.04 1.22-3.9.66-7.06-3.81-8.39zm2.09 7.89c-.77 2.57-2.44 5.62-4.11 8.68-2.1 3.83-4.2 7.66-4.68 10.81-.79 5.22-3.29 10.81-6.87 15.77-.59.83-1.22 1.64-1.87 2.42-.01.01-.03.03-.04.04-.06.08-.14.17-.22.26-3.15 3.73-6.91 6.95-10.99 9.18-1.6.86-3.24 1.59-5.18 2.11-1.95.54-4.22.89-7.08 1.03h-.02c-3-.15-5.44-.57-7.53-1.19-2.11-.62-3.89-1.46-5.61-2.46-4.85-2.85-9.21-7.08-12.57-11.86-3.42-4.85-5.78-10.25-6.54-15.31-.49-3.15-2.58-6.98-4.68-10.81-1.68-3.06-3.35-6.11-4.11-8.68-.88-2.9-.52-5.2 2.6-6.13 2.37-.72 1.99-2.77 1.55-5.11-.06-.33-.13-.68-.18-1.13l-.27-1.75c-1.22-7.96-.01-14.27 2.67-19.2 3.25-5.97 8.68-9.94 14.52-12.39 5.3-2.22 12.7-3.33 20.12-3.33 7.43-.01 14.84 1.1 20.15 3.3 5.85 2.45 11.28 6.42 14.55 12.4 2.7 4.93 3.92 11.24 2.7 19.21l-.27 1.77c-.06.44-.13.79-.19 1.12-.43 2.33-.81 4.38 1.55 5.11 3.11.95 3.46 3.24 2.6 6.14z"
          ></path>

          <circle
            cx="73"
            cy="73"
            r="72"
            stroke="#7864C7"
            style={{ strokeWidth: '0.5px' }}
            fill="none"
          />

          {includes(lowercaseAreas, 'forehead') && (
            <>
              <path
                fill={coloursArray[0 % size(coloursArray)]}
                d="m56.44 38.99c-.34-.37-.92-.41-1.3-.08-.37.34-.41.93-.08 1.3l1.47 1.66c.34.39.92.43 1.3.08.37-.33.41-.92.08-1.29z"
              ></path>
              <path
                fill={coloursArray[1 % size(coloursArray)]}
                d="m70.35 26.71h-2.45c-.32 0-.58.26-.58.58v2.45c0 .32.26.58.58.58h2.45c.31 0 .57-.26.57-.58v-2.45c0-.33-.26-.58-.57-.58z"
              ></path>
              <path
                fill={coloursArray[2 % size(coloursArray)]}
                d="m60.11 22.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[3 % size(coloursArray)]}
                d="m89.54 26.86c-.5-.58-1.38-.63-1.96-.13-.57.5-.62 1.38-.12 1.96l1.47 1.66c.5.57 1.38.62 1.95.12.58-.5.63-1.38.13-1.95z"
              ></path>
              <path
                fill={coloursArray[4 % size(coloursArray)]}
                d="m82.55 35.58h-.01c-.12-.1-.24-.18-.41-.21-.49-.1-.98.22-1.08.71l-.68 3.21c-.03.14-.01.28.03.43.13.49.63.79 1.12.64l3.26-.89c.21-.03.4-.13.54-.3.34-.39.28-.98-.1-1.32z"
              ></path>
            </>
          )}

          {includes(lowercaseAreas, 'temples') && (
            <>
              <path
                fill={coloursArray[5 % size(coloursArray)]}
                d="m44.44 54.99c-.34-.37-.92-.41-1.3-.08-.37.34-.41.93-.08 1.3l1.47 1.66c.34.39.92.43 1.3.08.37-.33.41-.92.08-1.29z"
              ></path>
              <path
                fill={coloursArray[6 % size(coloursArray)]}
                d="m97.35 54.71h-2.45c-.32 0-.58.26-.58.58v2.45c0 .32.26.58.58.58h2.45c.31 0 .57-.26.57-.58v-2.45c0-.33-.26-.58-.57-.58z"
              ></path>
              <path
                fill={coloursArray[7 % size(coloursArray)]}
                d="m48.11 60.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[8 % size(coloursArray)]}
                d="m102.54 57.86c-.5-.58-1.38-.63-1.96-.13-.57.5-.62 1.38-.12 1.96l1.47 1.66c.5.57 1.38.62 1.95.12.58-.5.63-1.38.13-1.95z"
              ></path>
            </>
          )}

          {includes(lowercaseAreas, 'cheeks') && (
            <>
              <path
                fill={coloursArray[9 % size(coloursArray)]}
                d="m92.44 77.99c-.34-.37-.92-.41-1.3-.08-.37.34-.41.93-.08 1.3l1.47 1.66c.34.39.92.43 1.3.08.37-.33.41-.92.08-1.29z"
              ></path>
              <path
                fill={coloursArray[10 % size(coloursArray)]}
                d="m62.35 82.71h-2.45c-.32 0-.58.26-.58.58v2.45c0 .32.26.58.58.58h2.45c.31 0 .57-.26.57-.58v-2.45c0-.33-.26-.58-.57-.58z"
              ></path>
              <path
                fill={coloursArray[11 % size(coloursArray)]}
                d="m100.11 77.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[12 % size(coloursArray)]}
                d="m56.11 76.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[13 % size(coloursArray)]}
                d="m97.54 86.86c-.5-.58-1.38-.63-1.96-.13-.57.5-.62 1.38-.12 1.96l1.47 1.66c.5.57 1.38.62 1.95.12.58-.5.63-1.38.13-1.95z"
              ></path>
              <path
                fill={coloursArray[14 % size(coloursArray)]}
                d="m53.55 86.58h-.01c-.12-.1-.24-.18-.41-.21-.49-.1-.98.22-1.08.71l-.68 3.21c-.03.14-.01.28.03.43.13.49.63.79 1.12.64l3.26-.89c.21-.03.4-.13.54-.3.34-.39.28-.98-.1-1.32z"
              ></path>
            </>
          )}

          {includes(lowercaseAreas, 'nose') && (
            <>
              <path
                fill={coloursArray[15 % size(coloursArray)]}
                d="m71.44 52.99c-.34-.37-.92-.41-1.3-.08-.37.34-.41.93-.08 1.3l1.47 1.66c.34.39.92.43 1.3.08.37-.33.41-.92.08-1.29z"
              ></path>
              <path
                fill={coloursArray[16 % size(coloursArray)]}
                d="m79.11 58.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[17 % size(coloursArray)]}
                d="m75.11 66.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
            </>
          )}

          {includes(lowercaseAreas, 'chin') && (
            <>
              <path
                fill={coloursArray[18 % size(coloursArray)]}
                d="m82.35 100.71h-2.45c-.32 0-.58.26-.58.58v2.45c0 .32.26.58.58.58h2.45c.31 0 .57-.26.57-.58v-2.45c0-.33-.26-.58-.57-.58z"
              ></path>
              <path
                fill={coloursArray[19 % size(coloursArray)]}
                d="m76.11 99.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[20 % size(coloursArray)]}
                d="m67.54 102.86c-.5-.58-1.38-.63-1.96-.13-.57.5-.62 1.38-.12 1.96l1.47 1.66c.5.57 1.38.62 1.95.12.58-.5.63-1.38.13-1.95z"
              ></path>
            </>
          )}

          {includes(lowercaseAreas, 'jawline') && (
            <>
              <path
                fill={coloursArray[21 % size(coloursArray)]}
                d="m60.35 100.71h-2.45c-.32 0-.58.26-.58.58v2.45c0 .32.26.58.58.58h2.45c.31 0 .57-.26.57-.58v-2.45c0-.33-.26-.58-.57-.58z"
              ></path>
              <path
                fill={coloursArray[22 % size(coloursArray)]}
                d="m90.35 100.71h-2.45c-.32 0-.58.26-.58.58v2.45c0 .32.26.58.58.58h2.45c.31 0 .57-.26.57-.58v-2.45c0-.33-.26-.58-.57-.58z"
              ></path>
              <path
                fill={coloursArray[23 % size(coloursArray)]}
                d="m98.11 95.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[24 % size(coloursArray)]}
                d="m52.11 95.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[25 % size(coloursArray)]}
                d="m76.11 105.51c-.19-.24-.55-.28-.81-.09l-1.56 1.28c-.24.21-.28.57-.08.81.19.24.55.28.81.08l1.56-1.26c.25-.21.29-.57.08-.82z"
              ></path>
              <path
                fill={coloursArray[26 % size(coloursArray)]}
                d="m83.54 103.86c-.5-.58-1.38-.63-1.96-.13-.57.5-.62 1.38-.12 1.96l1.47 1.66c.5.57 1.38.62 1.95.12.58-.5.63-1.38.13-1.95z"
              ></path>
              <path
                fill={coloursArray[27 % size(coloursArray)]}
                d="m67.54 104.86c-.5-.58-1.38-.63-1.96-.13-.57.5-.62 1.38-.12 1.96l1.47 1.66c.5.57 1.38.62 1.95.12.58-.5.63-1.38.13-1.95z"
              ></path>
            </>
          )}
        </g>
      </svg>
    </div>
  );
}
